/**
 * Thin progress bar at the bottom of the presenter view.
 *
 * Shows current slide position relative to total, plus visual "cut"
 * markers for the major narrative transitions:
 *   - Wechsel in den Interaktiven Workshop (slide 8)
 *   - Wechsel in das SVO OS (slide 21 — Live-Demo)
 *   - Reveal (slide 23 — Cliffhanger vor Reveal)
 */

import { useEffect, useState } from "react";
import type { ProgressCut } from "@/engine/app/offsite-config";
import { sollStatus } from "./soll-zeit";

interface Props {
  /** 0-based current slide index. */
  slideIndex: number;
  slideTotal: number;
  /** When true, presenter is in a workshop overlay (no slide visible). */
  workshopActive: boolean;
  /** 0-based slide index to jump to. When set, cuts become clickable. */
  onJump?: (index: number) => void;
  cuts: ProgressCut[];
  /** Soll-Endzeit der aktuellen Folie ("HH:MM"), siehe OffsiteConfig.sollZeiten. */
  sollZeit?: string;
}

/** Uhr, die alle 20 s neu rendert — reicht für Minutenanzeige. */
function useJetzt(aktiv: boolean): Date {
  const [jetzt, setJetzt] = useState(() => new Date());
  useEffect(() => {
    if (!aktiv) return;
    const id = setInterval(() => setJetzt(new Date()), 20000);
    return () => clearInterval(id);
  }, [aktiv]);
  return jetzt;
}

export function PresenterProgressBar({
  slideIndex,
  slideTotal,
  workshopActive,
  onJump,
  cuts,
  sollZeit,
}: Props) {
  const currentSlide = slideIndex + 1; // 1-based
  const jetzt = useJetzt(Boolean(sollZeit));
  const soll = sollZeit && !workshopActive ? sollStatus(sollZeit, jetzt) : null;
  const pct = (currentSlide / slideTotal) * 100;

  return (
    <div className="presenter-progress-bar">
      <div className="ppb-track" aria-hidden="true">
        <div className="ppb-fill" style={{ width: `${pct}%` }} />

        {cuts.map((c) => {
          // Place marker so fill lands exactly on it when currentSlide == atSlide.
          const cutPct = (c.atSlide / slideTotal) * 100;
          const passed = currentSlide >= c.atSlide;
          const className = `ppb-cut${passed ? " passed" : ""}${onJump ? " clickable" : ""}`;
          const inner = (
            <>
              <span className="ppb-cut-marker" />
              <span className="ppb-cut-label">
                <span className="ppb-cut-icon">{c.icon}</span>
                {c.label}
              </span>
            </>
          );
          if (onJump) {
            return (
              <button
                key={c.atSlide}
                type="button"
                className={className}
                style={{ left: `${cutPct}%` }}
                onClick={() => onJump(c.atSlide - 1)}
                title={`Springe zu Folie ${c.atSlide}`}
              >
                {inner}
              </button>
            );
          }
          return (
            <div
              key={c.atSlide}
              className={className}
              style={{ left: `${cutPct}%` }}
            >
              {inner}
            </div>
          );
        })}
      </div>

      <div className="ppb-meta">
        <span>
          {workshopActive ? (
            <strong>Workshop läuft</strong>
          ) : (
            <>
              Folie <strong>{currentSlide}</strong> / {slideTotal}
              {soll && (
                <span
                  className={`ppb-soll${soll.ueberMin > 0 ? " ueber" : ""}`}
                  data-testid="ppb-soll"
                >
                  {" · bis "}
                  {soll.text}
                  {soll.ueberMin > 0 && ` · +${soll.ueberMin}′`}
                </span>
              )}
            </>
          )}
        </span>
      </div>
    </div>
  );
}
